import '@classic/Shared/UI/Dialog/Dialog.scss';
import AlertsDialog from '@classic/Components/PopUpAlerts/Components/AlertsDialog';
import AlertBadge from '@classic/Components/PopUpAlerts/Components/AlertBadge';
import PopUpAlertsRepository from '@classic/Components/PopUpAlerts/Persistence/PopUpAlertsRepository';
import ViewedAlertsStore from '@classic/Components/PopUpAlerts/Persistence/ViewedAlertsStore';
import { createLogger } from '@classic/Shared/Logger';
import type { PopupAlert } from '@classic/Components/PopUpAlerts/Schemas';

const logger = createLogger('PopUpAlerts');
const DIALOG_MOUNT_ID = 'ss-alert-modal-area';

/** Baseline (`<dialog>` + custom elements, evergreen since 2022) — no polyfills. */
const isNativeDialogSupported = (): boolean =>
    typeof window.customElements !== 'undefined'
    && typeof HTMLDialogElement !== 'undefined'
    && typeof HTMLDialogElement.prototype.showModal === 'function';

/** Mirrors the legacy `$_GET['previewAlertID']` comma-list convention from design_header.php. */
const parsePreviewAlertIds = (url: URL): number[] => {
    const raw = url.searchParams.get('previewAlertID');
    if (!raw) {
        return [];
    }
    return raw
        .split(',')
        .map((id) => Number.parseInt(id.trim(), 10))
        .filter((id) => Number.isInteger(id) && id > 0);
};

/**
 * The API deliberately omits the `isAdminLoggedIn` exclusion the legacy SSR
 * flow applied (no session data is exposed to this public endpoint) — only
 * same-origin (avoids a self-redirect loop), login-page, and `adminArea`
 * exclusions are enforced here.
 * @see src/SmartSites/Domain/PopupAlert/Application/Query/GetActiveAlertsResult.php
 */
const shouldSkipDistrictRedirect = (districtUrl: string): boolean => {
    let target: URL;
    try {
        target = new URL(districtUrl, window.location.origin);
    } catch {
        return true;
    }
    if (target.origin === window.location.origin) {
        return true;
    }
    if (window.location.pathname === '/login') {
        return true;
    }
    return new URL(window.location.href).searchParams.has('adminArea');
};

const applyDistrictRedirectIfNeeded = (alerts: readonly PopupAlert[], districtUrl: string | null): boolean => {
    if (!districtUrl || !alerts.some((alert) => alert.forceDistrictRedirect)) {
        return false;
    }
    if (shouldSkipDistrictRedirect(districtUrl)) {
        return false;
    }
    window.location.assign(districtUrl);
    return true;
};

const init = async (): Promise<void> => {
    if (!isNativeDialogSupported()) {
        return;
    }

    const url = new URL(window.location.href);
    const previewAlertIds = parsePreviewAlertIds(url);
    const isPreview = previewAlertIds.length > 0;
    const forceShow = url.searchParams.has('showActiveAlerts');

    let response;
    try {
        response = await new PopUpAlertsRepository().fetchActiveAlerts(previewAlertIds);
    } catch (error) {
        logger.error('Failed to fetch active alerts', error);
        return;
    }

    if (applyDistrictRedirectIfNeeded(response.alerts, response.districtUrl)) {
        return;
    }

    const dialogMount = document.getElementById(DIALOG_MOUNT_ID) ?? document.body;
    const dialog = new AlertsDialog(dialogMount);

    const badge = new AlertBadge();
    badge.setVisible(response.alerts.length > 0);
    badge.setOnRecall(() => dialog.open(response.alerts));
    document.body.append(badge);

    // Preview/force-show bypass the viewed store entirely — an admin preview
    // or an explicit `?showActiveAlerts` request should never read or write
    // "already seen" state.
    const viewedStore = new ViewedAlertsStore();
    const alertsToShow = (isPreview || forceShow)
        ? response.alerts
        : viewedStore.getUnviewed(response.alerts);

    if (alertsToShow.length === 0) {
        return;
    }

    if (!isPreview && !forceShow) {
        viewedStore.markViewed(alertsToShow.map((alert) => alert.id));
    }

    dialog.open(alertsToShow);
};

init().catch((error: unknown) => logger.error('Unexpected error initializing popup alerts', error));
